import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { CheckOutlined, CloseOutlined } from '@material-ui/icons'
import ValueLabel from 'components/Common/ValueLabel/ValueLabel'
import { DefinitiveMoneyField } from 'components/DefinitiveInputs/DefinitiveMoneyField'
import { DefinitiveInputValidators } from 'components/DefinitiveInputs/Validators'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { MoneyString } from 'model/Money'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import {
  ManuallySetAdditionalCostsArgs,
  MoneyDto,
  PriceRowDto,
  PriceScope,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { useFinancialDetailsInputStyles } from './FinancialDetailsInput.styles'

export const AdditionalProdCostsInput = (props: {
  bomItemId: string
  priceRow: PriceRowDto
  priceScope: 'perUnit' | 'total'
  handleManualSaveAdditionalCosts: (
    rowId: string,
    args: ManuallySetAdditionalCostsArgs
  ) => Promise<void>
  handleResetAdditionalCosts: (
    rowId: string,
    primaryWorkingStep: WorkingStepType,
    secondaryWorkingStep: WorkingStepType
  ) => Promise<void>
  loading?: Record<string, boolean>
  defaultMoneyObject: MoneyDto
  variant?: 'caption' | 'body2'
}) => {
  const [editing, setEditing] = useState(false)
  const [additionalCosts, setAdditionalCosts] = useState(
    props.priceRow?.additionalProductionCostsManuallySet ||
      props.priceRow?.additionalProductionCosts
  )

  const { t } = useTranslation()
  const classes = useFinancialDetailsInputStyles()

  const projectIsEditable = useAppSelector(
    projectSelectors.ProjectEditableStateSelector
  )

  useEffect(() => {
    if (!editing) {
      setAdditionalCosts(
        props.priceRow?.additionalProductionCostsManuallySet ||
          props.priceRow?.additionalProductionCosts
      )
    }

    // return () => {
    //   setAdditionalCosts(undefined)
    //   setEditing(false)
    // }
  }, [
    editing,
    props.priceRow?.additionalProductionCostsManuallySet,
    props.priceRow?.additionalProductionCosts,
  ])

  const [changed, setChanged] = useState(false)

  const handleChange = (e: { currentTarget: { value: MoneyDto } }) => {
    setChanged(true)
    setAdditionalCosts({
      currency: e.currentTarget.value.currency,
      symbol: e.currentTarget.value.symbol,
      value: isNaN(e.currentTarget.value.value)
        ? null
        : e.currentTarget.value.value,
    })
  }

  const handleSave = async (e: { preventDefault: () => void }) => {
    try {
      e.preventDefault()

      if (additionalCosts.value >= 0) {
        await props.handleManualSaveAdditionalCosts(props.bomItemId, {
          primaryWorkingStep: props.priceRow.workingStep.primaryWorkingStep,
          secondaryWorkingStep: props.priceRow.workingStep.secondaryWorkingStep,
          additionalCosts: additionalCosts,
          priceScope:
            props.priceScope === 'perUnit' ? PriceScope.Unit : PriceScope.Total,
        })

        setEditing(false)
        setChanged(false)
      }
    } catch (ex) {
      ShowException('Failed to edit production time', ex)
    }
  }

  const handleClose = () => {
    setEditing(false)
    setChanged(false)
  }

  // shared costs should not be editable
  if (!editing || props.priceRow.costsAreShared) {
    return (
      <div style={{ textAlign: 'right', position: 'relative', width: '100%' }}>
        <Tooltip
          title={
            <>
              {`${t('project:costs-additional')}: ${MoneyString(
                additionalCosts,
                10
              )}`}
            </>
          }
        >
          <span>
            <ValueLabel
              onClick={
                !projectIsEditable || props.priceRow.costsAreShared
                  ? undefined
                  : () => setEditing(true)
              }
              valueProps={{
                variant: props.variant || 'body2',
              }}
              // label={'prod time'}
              value={MoneyString(additionalCosts)}
            />
          </span>
        </Tooltip>

        {props.priceRow.additionalProductionCostsManuallySet && (
          <Tooltip
            title={t(
              'project:reset-additional-costs',
              'reset to estimated additional costs',
              { costs: MoneyString(props.priceRow.additionalProductionCosts) }
            )}
          >
            <span>
              <Typography
                variant="caption"
                component="p"
                className={classes.resetValueButton}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  right: 0,
                  bottom: '-1.25em',
                }}
                onClick={() =>
                  props.handleResetAdditionalCosts(
                    props.bomItemId,
                    props.priceRow.workingStep.primaryWorkingStep,
                    props.priceRow.workingStep.secondaryWorkingStep
                  )
                }
              >
                {props.loading[
                  `delete-manual-additional-costs-${props.priceRow.workingStep.primaryWorkingStep}-${props.priceRow.workingStep.secondaryWorkingStep}`
                ] ? (
                  <CircularProgress size={20} />
                ) : (
                  MoneyString(props.priceRow.additionalProductionCosts)
                )}
              </Typography>
            </span>
          </Tooltip>
        )}
      </div>
    )
  } else {
    return (
      <form onSubmit={handleSave}>
        <div
          style={{
            display: 'flex',
            gap: '.5rem',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <DefinitiveMoneyField
            onChange={(newMoney) =>
              handleChange({ currentTarget: { value: newMoney } })
            }
            moneyDto={additionalCosts || props.defaultMoneyObject}
            typographyVariant={props.variant || 'body2'}
            numberFormatProps={{
              thousandSeparator: true,
            }}
            selectTextOnFirstRender
            isAllowed={DefinitiveInputValidators.IsMinMaxAllowed(0, 999999)}
            validation={(newMoney: MoneyDto) => {
              const validations = DefinitiveInputValidators.CombineValidators([
                DefinitiveInputValidators.IsRequired(newMoney.value),
                DefinitiveInputValidators.MinValue(newMoney.value, 0),
              ])

              if (validations?.length > 0) {
                setChanged(false)
              }

              return validations
            }}
            disableWarningsIcon
            showRawValueInTooltip={(value: MoneyDto) => MoneyString(value, 20)}
          />
          {props.loading[
            `manual-set-additional-costs-${props.priceRow.workingStep.primaryWorkingStep}-${props.priceRow.workingStep.secondaryWorkingStep}`
          ] ? (
            <CircularProgress size={20} style={{ display: 'inline-flex' }} />
          ) : (
            <>
              <IconButton
                type="submit"
                color={'primary'}
                size="small"
                disabled={!changed}
              >
                <CheckOutlined />
              </IconButton>
              <IconButton type="reset" onClick={handleClose} size="small">
                <CloseOutlined />
              </IconButton>
            </>
          )}
        </div>
      </form>
    )
  }
}
